import * as React from "react"
import { Combobox as ComboboxPrimitive } from "@base-ui/react"
import { useVirtualizer, type Virtualizer } from "@tanstack/react-virtual"

import { ComboboxItem, ComboboxList } from "@/components/ui/combobox"
import { cn } from "cn"

type VirtualizerInstance = Virtualizer<HTMLDivElement, Element>

interface VirtualizedComboboxListProps<T> {
  open: boolean
  /** Approximate row height in px (used until measured). */
  estimateSize?: number
  /** Max list viewport height in px. */
  maxHeight?: number
  overscan?: number
  /**
   * When false, skip per-row measurement (faster; use when row height is fixed).
   * Defaults to false.
   */
  measureRows?: boolean
  getItemKey?: (item: T, index: number) => string | number
  children: (item: T, index: number) => React.ReactNode
  className?: string
  /** Merged onto each `ComboboxItem` (e.g. `items-start` for multi-line rows). */
  itemClassName?: string
  virtualizerRef?: React.MutableRefObject<VirtualizerInstance | null>
}

/**
 * Virtualized list body for large Combobox option sets.
 *
 * Upstream: Base UI Combobox `virtualized` prop + virtualized demo
 * (https://base-ui.com/react/components/combobox#virtualized) using
 * `@tanstack/react-virtual` and `Combobox.useFilteredItems`.
 * Requires `<Combobox virtualized …>` on the root.
 *
 * Scrollport sizing mirrors Base UI's demo: fixed `height` from `--total-size`,
 * and `max-height: var(--available-height)` only as a cap.
 * Do not put `--available-height` inside `min()` with px — during popup
 * measure Base UI sets it to `max-content`, which invalidates the declaration
 * and expands the list to full content height (rendering every row).
 *
 * Row refs: React 19 passes `ref` as a normal prop into ComboboxItem (`...props`).
 */
export function VirtualizedComboboxList<T>({
  open,
  estimateSize = 36,
  maxHeight = 288,
  overscan = 6,
  measureRows = false,
  getItemKey,
  children,
  className,
  itemClassName,
  virtualizerRef,
}: VirtualizedComboboxListProps<T>) {
  const filteredItems = ComboboxPrimitive.useFilteredItems<T>()
  const scrollElementRef = React.useRef<HTMLDivElement | null>(null)

  // TanStack Virtual returns unmemoizable fns; React Compiler skips this component (expected).
  // eslint-disable-next-line react-hooks/incompatible-library -- useVirtualizer API
  const virtualizer = useVirtualizer({
    enabled: open,
    count: filteredItems.length,
    getScrollElement: () => scrollElementRef.current,
    estimateSize: () => estimateSize,
    overscan,
    paddingStart: 4,
    paddingEnd: 4,
    scrollPaddingStart: 4,
    scrollPaddingEnd: 4,
  })

  React.useImperativeHandle(virtualizerRef, () => virtualizer, [virtualizer])

  const handleScrollElementRef = React.useCallback(
    (element: HTMLDivElement | null) => {
      scrollElementRef.current = element
    },
    []
  )

  const totalSize = virtualizer.getTotalSize()

  return (
    <ComboboxList className={cn("max-h-none overflow-visible p-0", className)}>
      {filteredItems.length === 0 ? null : (
        <div
          role="presentation"
          ref={handleScrollElementRef}
          className="overflow-auto overscroll-contain"
          style={
            {
              height: `min(${maxHeight}px, var(--total-size))`,
              maxHeight: "var(--available-height)",
              ["--total-size" as string]: `${totalSize}px`,
            } as React.CSSProperties
          }
        >
          <div
            role="presentation"
            className="relative w-full"
            style={{ height: totalSize }}
          >
            {virtualizer.getVirtualItems().map((virtualItem) => {
              const item = filteredItems[virtualItem.index]
              if (item == null) return null

              return (
                <ComboboxItem
                  key={
                    getItemKey
                      ? getItemKey(item, virtualItem.index)
                      : virtualItem.key
                  }
                  index={virtualItem.index}
                  data-index={virtualItem.index}
                  ref={measureRows ? virtualizer.measureElement : undefined}
                  value={item as never}
                  aria-setsize={filteredItems.length}
                  aria-posinset={virtualItem.index + 1}
                  className={cn("absolute top-0 left-0 w-full", itemClassName)}
                  style={{
                    height: virtualItem.size,
                    transform: `translateY(${virtualItem.start}px)`,
                  }}
                >
                  {children(item, virtualItem.index)}
                </ComboboxItem>
              )
            })}
          </div>
        </div>
      )}
    </ComboboxList>
  )
}

/** Keep highlighted row in view while keyboard-navigating a virtualized list. */
export function scrollVirtualizedHighlight(
  virtualizer: VirtualizerInstance | null | undefined,
  index: number | undefined,
  reason: string
) {
  if (!virtualizer || index == null || index < 0) return

  const isStart = index === 0
  const isEnd = index === virtualizer.options.count - 1
  const shouldScroll =
    reason === "none" || (reason === "keyboard" && (isStart || isEnd))

  if (!shouldScroll && reason !== "keyboard") return

  queueMicrotask(() => {
    virtualizer.scrollToIndex(index, {
      align: isEnd ? "start" : reason === "keyboard" ? "auto" : "end",
    })
  })
}
